// TunnelHealthBoard.jsx - Network Topology -> Tunnels & WAN: one trusted verdict per link.
// Data: GET /api/net/link-health?days=1|7|30|90 and /api/net/link-health/history (backend/netLinkHealth.js).
// The raw panels (live polls, Firebox SNMP, CloudWatch per tunnel, per-tunnel history, path analysis)
// are kept below in collapsed "Raw sources" sections for investigation only.
// UI text is literal English (not i18n), like the other Network Topology panels.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, XCircle, MinusCircle, ShieldCheck, Info, Activity } from "lucide-react";
import GreTunnelPanel from "./GreTunnelPanel";
import BovpnJakartaMap from "../components/BovpnJakartaMap";
import VpnCloudWatchPanel from "./VpnCloudWatchPanel";
import VpnAvailabilityHistory from "./VpnAvailabilityHistory";
import VpnPathPanel from "./VpnPathPanel";

const API_BASE = window.__API_BASE__ || "";
const get = async (p) => {
  const r = await fetch(API_BASE + p, { headers: { Authorization: "Bearer " + (localStorage.getItem("auth_token") || "") } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "HTTP " + r.status);
  return j;
};
const post = async (p) => {
  const r = await fetch(API_BASE + p, { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem("auth_token") || "") } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "HTTP " + r.status);
  return j;
};

const C = { ok: "#16a34a", up: "#16a34a", attention: "#d97706", partial: "#d97706", down: "#dc2626", inactive: "#94a3b8", unknown: "#64748b", nodata: "#e2e8f0" };
const VERDICT = { ok: "Healthy", attention: "Needs attention", down: "Down", inactive: "Not in use" };
const NOW = { up: "Up", partial: "Up · redundancy lost", down: "Down", inactive: "Not in use", unknown: "Unknown" };
const CONF = {
  verified: { label: "Verified", color: "#0f766e", tip: "An independent source agrees with the primary source." },
  single: { label: "Single source", color: "#64748b", tip: "Only one source measures this link; nothing independent to compare with." },
  conflict: { label: "Sources disagree", color: "#b91c1c", tip: "An independent source disagrees with the primary source. Investigate before trusting the number." },
  stale: { label: "Stale", color: "#b45309", tip: "The live status is older than expected; the poll may be failing." },
};
const RANGES = [[1, "24h"], [7, "7 days"], [30, "30 days"], [90, "90 days"]];
const card = { background: "var(--card-bg, #fff)", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 };
const chip = (c, solid) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
  color: solid ? "#fff" : c, background: solid ? c : c + "1a", border: "1px solid " + (solid ? c : c + "55") });
const th = { padding: "6px 8px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: ".03em", whiteSpace: "nowrap" };
const td = { padding: "7px 8px", fontSize: 12.5, verticalAlign: "top", borderTop: "1px solid #f1f5f9" };
const btn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 12.5, cursor: "pointer", border: "1px solid #cbd5e1", background: "#fff", color: "#334155" };

const wib = (ms, withYear) => (ms ? new Date(ms).toLocaleString("en-GB", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", ...(withYear ? { year: "numeric" } : {}), hour: "2-digit", minute: "2-digit" }) : "-");
const fmtMin = (m) => { const v = Math.round(m || 0); if (v < 60) return v + " min"; if (v < 1440) return Math.floor(v / 60) + "h " + String(v % 60).padStart(2, "0") + "m"; return Math.floor(v / 1440) + "d " + Math.floor((v % 1440) / 60) + "h"; };
const pct = (v) => (v == null ? "—" : (v >= 99.995 && v < 100 ? "99.99" : v.toFixed(2)) + "%");
const pctColor = (v, target) => (v == null ? "#94a3b8" : v >= 99.9 ? C.ok : v >= target ? "#ca8a04" : C.down);
const ago = (min) => (min == null ? "no data" : min < 1 ? "just now" : min < 60 ? min + " min ago" : min < 1440 ? Math.round(min / 60) + " h ago" : Math.round(min / 1440) + " d ago");

function VerdictIcon({ v, size = 16 }) {
  if (v === "down") return <XCircle size={size} color={C.down} />;
  if (v === "attention") return <AlertTriangle size={size} color={C.attention} />;
  if (v === "inactive") return <MinusCircle size={size} color={C.inactive} />;
  return <CheckCircle2 size={size} color={C.ok} />;
}
function NowBadge({ now, inactive }) {
  const s = inactive ? "inactive" : (now && now.state) || "unknown";
  return <span style={chip(C[s] || C.unknown)} title={now ? now.source + " · " + wib(now.at) : ""}><span style={{ width: 7, height: 7, borderRadius: 9, background: C[s] || C.unknown }} />{NOW[s] || s}</span>;
}
function ConfBadge({ c }) {
  const x = CONF[c] || CONF.single;
  return <span style={chip(x.color)} title={x.tip}>{c === "verified" ? <ShieldCheck size={11} /> : c === "conflict" ? <AlertTriangle size={11} /> : <Info size={11} />}{x.label}</span>;
}

// Strip: one block per bucket. Green up, amber redundancy lost, red down (any period), grey no data.
function Strip({ buckets, bucketMs, height = 14, max }) {
  if (!buckets || !buckets.length) return <span style={{ fontSize: 11, color: "#94a3b8" }}>no data</span>;
  const b = max && buckets.length > max ? buckets.slice(buckets.length - max) : buckets;
  return (
    <div style={{ display: "flex", gap: 1, height, minWidth: 80 }}>
      {b.map((x) => (
        <div key={x.t} title={wib(x.t) + " – " + wib(x.t + bucketMs) + " WIB · " + (x.s === "nodata" ? "no data" : x.s === "down" ? "down at least once (" + pct(x.pct) + " up)" : x.s === "partial" ? "up, redundancy lost at times" : "up")}
          style={{ flex: 1, minWidth: 2, borderRadius: 2, background: C[x.s] || C.nodata }} />
      ))}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: ".04em" }}>{title}</div>
        {right && <div style={{ marginLeft: "auto", fontSize: 11.5, color: "#64748b" }}>{right}</div>}
      </div>
      {children}
    </div>
  );
}

function EventTable({ rows, kind, emptyText }) {
  const [all, setAll] = useState(false);
  if (!rows || !rows.length) return <div style={{ fontSize: 12.5, color: "#16a34a", display: "flex", alignItems: "center", gap: 6 }}><CheckCircle2 size={14} /> {emptyText}</div>;
  const list = [...rows].reverse();
  const shown = all ? list : list.slice(0, 10);
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={th}>Started (WIB)</th><th style={th}>Ended (WIB)</th><th style={{ ...th, textAlign: "right" }}>Duration</th><th style={th}>{kind === "partial" ? "Down member" : "Affected"}</th><th style={th}>Reason (AWS tunnel log)</th></tr></thead>
        <tbody>
          {shown.map((e, i) => (
            <tr key={i}>
              <td style={{ ...td, whiteSpace: "nowrap" }}>{wib(e.start)}</td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>{e.ongoing ? <span style={chip(kind === "partial" ? C.attention : C.down)}>ongoing</span> : wib(e.end)}</td>
              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>{fmtMin(e.minutes)}{e.minutes < 15 && !e.ongoing ? <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 400 }}>short</div> : null}</td>
              <td style={{ ...td, fontFamily: "monospace", fontSize: 11.5 }}>{(e.members || []).join(", ") || "—"}</td>
              <td style={{ ...td, color: "#475569", fontSize: 12 }}>{e.reason || <span style={{ color: "#94a3b8" }}>—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.length > 10 && <button style={{ ...btn, marginTop: 6, padding: "3px 10px", fontSize: 12 }} onClick={() => setAll(!all)}>{all ? "Show latest 10" : "Show all " + list.length}</button>}
    </div>
  );
}

function LinkDetail({ l, data }) {
  const A = l.A;
  const RES = { agree: ["Agrees", "#0f766e"], disagree: ["Disagrees", "#b91c1c"], nodata: ["Not enough data", "#64748b"] };
  return (
    <div style={{ padding: "4px 4px 12px" }}>
      {l.why && l.why.length > 0 && (
        <div style={{ background: l.verdict === "down" ? "#fef2f2" : "#fffbeb", border: "1px solid " + (l.verdict === "down" ? "#fecaca" : "#fde68a"), borderRadius: 8, padding: "8px 10px", fontSize: 12.5, color: "#7c2d12" }}>
          {l.why.map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, marginTop: 10 }}>
        {[
          ["Now", l.now ? (NOW[l.now.state] || l.now.state) + (l.now.text ? " · " + l.now.text : "") : "—", l.now ? l.now.source + " · " + ago(l.now.ageMin) : ""],
          ["Availability", pct(A.availability), A.upN + A.downN + " periods of 5 min · coverage " + (A.coverage != null ? Math.round(A.coverage) + "%" : "—")],
          ["Outages", A.outages.length ? A.outages.length + " · " + fmtMin(A.outageMinutes) : "none", A.longest ? "longest " + fmtMin(A.longest) : "link never fully down"],
          ["Redundancy lost", A.partials.length ? A.partials.length + " time(s) · " + fmtMin(A.partialMinutes) : "never", "link stayed up; not counted as downtime"],
        ].map(([k, v, s]) => (
          <div key={k} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px" }}>
            <div style={{ fontSize: 11, color: "#64748b" }}>{k}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{v}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{s}</div>
          </div>
        ))}
      </div>
      <Section title="Timeline" right={wib(data.from) + " → " + wib(data.to) + " WIB · 1 block = " + fmtMin(data.bucketMs / 60000)}>
        <Strip buckets={A.buckets} bucketMs={data.bucketMs} height={22} />
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
          <span style={{ color: C.up }}>■</span> up · <span style={{ color: C.partial }}>■</span> up, redundancy lost · <span style={{ color: C.down }}>■</span> down at least once · <span style={{ color: "#cbd5e1" }}>■</span> no data. Hover a block for its time.
        </div>
      </Section>
      <Section title="Outages — link fully down" right={"Primary source: " + l.source}>
        <EventTable rows={A.outages} kind="outage" emptyText="No outage in this window." />
      </Section>
      {A.partials.length > 0 && (
        <Section title="Redundancy lost — link stayed up">
          <EventTable rows={A.partials} kind="partial" emptyText="" />
        </Section>
      )}
      {l.tunnels && l.tunnels.length > 0 && (
        <Section title={l.kind === "bovpn" ? "Peers" : "Tunnels"}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>{l.kind === "bovpn" ? "Peer" : "Outside IP"}</th><th style={th}>Role</th><th style={{ ...th, textAlign: "right" }}>{l.kind === "bovpn" ? "Has SA" : "Up"} in window</th><th style={th}>Now</th>{l.kind === "aws" && <th style={th}>Last UP seen (CloudWatch)</th>}</tr></thead>
              <tbody>{l.tunnels.map((t) => (
                <tr key={t.name}>
                  <td style={{ ...td, fontFamily: "monospace" }}>{t.name}</td>
                  <td style={td}>{t.role ? t.role : t.inUse ? "in use" : <span style={{ color: "#94a3b8" }}>not in use (ignored)</span>}</td>
                  <td style={{ ...td, textAlign: "right" }}>{pct(t.availability)}</td>
                  <td style={td}>{t.now ? <span style={chip(t.now === "up" ? C.up : t.inUse ? C.down : C.inactive)}>{t.now}</span> : "—"}</td>
                  {l.kind === "aws" && <td style={{ ...td, whiteSpace: "nowrap" }}>{t.lastUp ? wib(t.lastUp) : "not in 30 days"}</td>}
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Section>
      )}
      {l.routesNow && (
        <Section title="Routes with an SA now" right="routes without an SA are idle, not down">
          {l.routesNow.length ? <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{l.routesNow.map((r, i) => <span key={i} style={{ ...chip("#334155"), fontFamily: "monospace", fontWeight: 500 }}>{r.local} → {r.remote}</span>)}</div>
            : <div style={{ fontSize: 12.5, color: C.down }}>No SA to this gateway in the latest poll.</div>}
        </Section>
      )}
      <Section title="Cross-checks with independent sources" right="change the confidence badge only">
        {l.checks && l.checks.length ? l.checks.map((c) => (
          <div key={c.key} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", borderTop: "1px solid #f1f5f9" }}>
            <span style={{ ...chip(RES[c.result][1]), marginTop: 1 }}>{RES[c.result][0]}{c.pct != null ? " · " + c.pct + "%" : ""}</span>
            <div style={{ fontSize: 12.5 }}>
              <div style={{ fontWeight: 600, color: "#1e293b" }}>{c.label}</div>
              <div style={{ color: "#475569" }}>{c.text}</div>
              <div style={{ color: "#94a3b8", fontSize: 11 }}>{c.source}</div>
            </div>
          </div>
        )) : <div style={{ fontSize: 12.5, color: "#64748b" }}>No independent source exists for this link, so the number relies on {l.source} alone.</div>}
        {l.devNow && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Latest device probe round: {l.devNow.ok} of {l.devNow.n} device(s) answered · {ago(l.devNow.ageMin)}.</div>}
      </Section>
      {l.notes && l.notes.length > 0 && (
        <Section title="Notes">
          {l.notes.map((n, i) => <div key={i} style={{ fontSize: 12, color: "#475569", marginBottom: 3 }}>• {n}</div>)}
        </Section>
      )}
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>{l.path} · {l.sub}</div>
    </div>
  );
}

function SiteCard({ site, links, data, openId, setOpenId }) {
  const comb = site.combined;
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", borderTop: "4px solid " + (C[site.verdict] || C.unknown) }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "12px 14px 8px" }}>
        <VerdictIcon v={site.verdict} />
        <span style={{ fontWeight: 700, fontSize: 15 }}>{site.label}</span>
        <span style={{ fontSize: 12, color: "#64748b" }}>{site.sub}</span>
        {comb && (
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#475569" }} title={"Up when any of: " + comb.members.join(" · ")}>
            Site reachable from AWS (any of its {comb.members.length} VPNs): <b style={{ color: pctColor(comb.A.availability, data.target) }}>{pct(comb.A.availability)}</b>
            {comb.A.outages.length ? " · " + comb.A.outages.length + " outage(s), " + fmtMin(comb.A.outageMinutes) : " · no outage"}
          </span>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead><tr>
            <th style={{ ...th, width: 24 }} />
            <th style={th}>Link</th><th style={th}>Now</th>
            <th style={{ ...th, textAlign: "right" }}>Availability</th><th style={{ ...th, width: "22%" }}>Timeline</th>
            <th style={{ ...th, textAlign: "right" }}>Outages</th><th style={th}>Confidence</th>
          </tr></thead>
          <tbody>
            {links.map((l) => {
              const open = openId === l.id;
              const A = l.A;
              return (
                <React.Fragment key={l.id}>
                  <tr id={"lh-" + l.id.replace(/[^a-z0-9]/gi, "_")} onClick={() => setOpenId(open ? null : l.id)} style={{ cursor: "pointer", background: open ? "#f8fafc" : "transparent", opacity: l.verdict === "inactive" ? 0.75 : 1 }}>
                    <td style={td}>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}><VerdictIcon v={l.verdict} size={14} /><span style={{ fontWeight: 600, color: "#0f172a" }}>{l.title}</span></div>
                      <div style={{ fontSize: 11.5, color: l.verdict === "ok" ? "#94a3b8" : l.verdict === "inactive" ? "#94a3b8" : C[l.verdict], marginLeft: 20 }}>
                        {l.why && l.why.length ? l.why[0] : l.kind === "aws" ? "AWS Site-to-Site VPN" : l.kind === "bovpn" ? "WatchGuard BOVPN" : "GRE tunnel"}
                      </div>
                    </td>
                    <td style={td}><NowBadge now={l.now} inactive={l.verdict === "inactive"} /></td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700, color: l.verdict === "inactive" ? "#94a3b8" : pctColor(A.availability, data.target) }}>{l.verdict === "inactive" ? "—" : pct(A.availability)}</td>
                    <td style={td}>{l.verdict === "inactive" ? <span style={{ fontSize: 11, color: "#94a3b8" }}>not in use</span> : <Strip buckets={A.buckets} bucketMs={data.bucketMs} max={60} />}</td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{l.verdict === "inactive" ? "—" : A.outages.length ? <span><b style={{ color: C.down }}>{A.outages.length}</b> <span style={{ color: "#64748b", fontSize: 11.5 }}>{fmtMin(A.outageMinutes)}</span></span> : <span style={{ color: "#94a3b8" }}>0</span>}</td>
                    <td style={td}><ConfBadge c={l.confidence} /></td>
                  </tr>
                  {open && <tr><td colSpan={7} style={{ padding: "0 14px 6px", background: "#f8fafc" }}><LinkDetail l={l} data={data} /></td></tr>}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthlyHistory({ links }) {
  const [d, setD] = useState(null), [err, setErr] = useState(""), [busy, setBusy] = useState(false), [msg, setMsg] = useState("");
  const load = useCallback(() => { setErr(""); get("/api/net/link-health/history?months=12").then(setD).catch((e) => setErr(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  const rebuild = async () => { setBusy(true); setMsg(""); try { const r = await post("/api/net/link-health/history/rebuild"); setMsg("Recalculated " + r.days + " day(s) from raw data."); load(); } catch (e) { setErr(e.message); } setBusy(false); };
  const view = useMemo(() => {
    if (!d) return null;
    const months = [...new Set(d.rows.filter((r) => r.availability != null).map((r) => r.ym))].sort();
    const by = {};
    d.rows.forEach((r) => { (by[r.link_id] = by[r.link_id] || { id: r.link_id, title: r.title, kind: r.kind, inactive: true, m: {} }).m[r.ym] = r; if (!r.inactive) by[r.link_id].inactive = false; });
    const order = links.map((l) => l.id);
    const rows = Object.values(by).sort((a, b) => (order.indexOf(a.id) + 1 || 99) - (order.indexOf(b.id) + 1 || 99));
    return { months, rows };
  }, [d, links]);
  if (err) return <div style={{ color: C.down, fontSize: 12.5 }}>{err}</div>;
  if (!view) return <div style={{ fontSize: 12.5, color: "#64748b" }}>Loading…</div>;
  return (
    <div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
        Same rules as the table above, saved per day (WIB) and kept permanently. History starts {d.firstDay || "—"}; AWS links go back up to 90 days (CloudWatch retention), BOVPN gateways only to when gateway samples started.
        <button style={{ ...btn, marginLeft: 8, padding: "2px 8px", fontSize: 11.5 }} disabled={busy} onClick={rebuild}>{busy ? "Recalculating…" : "Recalculate last 90 days"}</button>
        {msg && <span style={{ color: C.ok, marginLeft: 8 }}>{msg}</span>}
      </div>
      {!view.rows.length ? <div style={{ fontSize: 12.5, color: "#64748b" }}>No daily rows yet — they are written automatically a few minutes after the server starts.</div> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Link</th>{view.months.map((m) => <th key={m} style={{ ...th, textAlign: "right" }}>{m}</th>)}</tr></thead>
            <tbody>{view.rows.filter((r) => !r.inactive).map((r) => (
              <tr key={r.id}>
                <td style={td}>{r.title}</td>
                {view.months.map((m) => { const x = r.m[m]; return (
                  <td key={m} style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }} title={x ? x.days + " day(s) · " + x.outages + " outage(s) · " + fmtMin(x.om) + " down" : "no data"}>
                    {x && x.availability != null ? <><b style={{ color: pctColor(x.availability, d.target) }}>{pct(x.availability)}</b>{x.outages ? <div style={{ fontSize: 10.5, color: "#64748b" }}>{x.outages}× · {fmtMin(x.om)}</div> : null}</> : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>); })}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Collapsible({ title, sub, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff", overflow: "hidden" }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "transparent", border: 0, cursor: "pointer", textAlign: "left", flexWrap: "wrap" }}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span style={{ fontWeight: 700, fontSize: 13.5, color: "#1e293b" }}>{title}</span>
        {sub && <span style={{ fontSize: 12, color: "#64748b" }}>{sub}</span>}
      </button>
      {open && <div style={{ padding: "0 12px 12px" }}>{children}</div>}
    </div>
  );
}

const SRC_LABEL = { cloudwatch: "AWS CloudWatch", telemetry: "AWS VgwTelemetry", snmp: "Firebox SNMP", gre: "GRE SSM check", probes: "Site device probes" };

export default function TunnelHealthBoard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null), [err, setErr] = useState(""), [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback((refresh) => {
    setBusy(true); setErr("");
    get("/api/net/link-health?days=" + days + (refresh ? "&refresh=1" : "")).then(setData).catch((e) => setErr(e.message)).finally(() => setBusy(false));
  }, [days]);
  useEffect(() => { load(false); const t = setInterval(() => load(false), 5 * 60e3); return () => clearInterval(t); }, [load]);
  const byId = useMemo(() => new Map((data ? data.links : []).map((l) => [l.id, l])), [data]);
  const issues = useMemo(() => (data ? data.links.filter((l) => l.verdict === "down" || l.verdict === "attention").sort((a, b) => (a.verdict === "down" ? 0 : 1) - (b.verdict === "down" ? 0 : 1)) : []), [data]);
  const open = (id) => { setOpenId(id); setTimeout(() => { const el = document.getElementById("lh-" + id.replace(/[^a-z0-9]/gi, "_")); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 50); };
  const S = data && data.summary;
  const label = (RANGES.find((r) => r[0] === days) || [0, ""])[1];

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Activity size={20} color="rgb(var(--brand-600, 13 148 136))" />
        <div style={{ marginRight: "auto" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Tunnels &amp; WAN health</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>One verdict per link, from one rule set. Click a link for its outages, tunnels and cross-checks.</div>
        </div>
        <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
          {RANGES.map(([d, l]) => <button key={d} onClick={() => setDays(d)} style={{ padding: "4px 12px", border: 0, borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer", background: days === d ? "#fff" : "transparent", color: days === d ? "#0f172a" : "#64748b", boxShadow: days === d ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>{l}</button>)}
        </div>
        <button style={btn} onClick={() => load(true)} disabled={busy}><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh</button>
      </div>

      {err && <div style={{ ...card, color: C.down }}>{err}</div>}
      {!data && !err && <div style={card}>Loading link health…</div>}

      {data && <>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          {[["down", S.down, "Down now"], ["attention", S.attention, "Need attention"], ["ok", S.ok, "Healthy"], ["inactive", S.inactive, "Configured, not in use"]].map(([k, n, l]) => (
            <div key={k} style={{ ...card, padding: "10px 14px", borderLeft: "4px solid " + C[k] }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: n && k !== "ok" && k !== "inactive" ? C[k] : "#0f172a" }}>{n}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{l}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: 11.5, color: "#64748b" }}>
          <span>Data freshness:</span>
          {Object.entries(data.fresh || {}).map(([k, f]) => {
            const okF = f.ageMin != null && f.ageMin <= f.limit;
            return <span key={k} style={chip(okF ? "#0f766e" : C.attention)} title={"Expected at most " + f.limit + " min old"}>{SRC_LABEL[k] || k} · {ago(f.ageMin)}</span>;
          })}
          <span style={{ marginLeft: "auto" }}>Window {label} · generated {wib(data.generatedAt)} WIB{data.cached ? " (cached)" : ""}</span>
        </div>

        <div style={{ ...card, borderLeft: "4px solid " + (issues.length ? (S.down ? C.down : C.attention) : C.ok) }}>
          <div style={{ fontWeight: 700, marginBottom: issues.length ? 8 : 0, display: "flex", alignItems: "center", gap: 8 }}>
            {issues.length ? <AlertTriangle size={16} color={S.down ? C.down : C.attention} /> : <CheckCircle2 size={16} color={C.ok} />}
            {issues.length ? issues.length + " link(s) need a look" : "All links in use are healthy in this window"}
          </div>
          {issues.map((l) => (
            <div key={l.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0", borderTop: "1px solid #f1f5f9" }}>
              <VerdictIcon v={l.verdict} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{l.title} <ConfBadge c={l.confidence} /></div>
                {l.why.map((w, i) => <div key={i} style={{ fontSize: 12.5, color: "#475569" }}>{w}</div>)}
              </div>
              <button style={{ ...btn, padding: "3px 10px", fontSize: 12 }} onClick={() => open(l.id)}>Details</button>
            </div>
          ))}
        </div>

        {data.sites.map((s) => (
          <SiteCard key={s.key} site={s} links={s.links.map((id) => byId.get(id)).filter(Boolean)} data={data} openId={openId} setOpenId={setOpenId} />
        ))}

        <Collapsible title="Monthly availability per link" sub="permanent history, same rules as above">
          <MonthlyHistory links={data.links} />
        </Collapsible>

        <Collapsible title="How these numbers are calculated" sub="rules, sources and confidence badges">
          <div style={{ fontSize: 12.5, color: "#334155", display: "grid", gap: 4 }}>
            {data.method.rules.map((r, i) => <div key={i}>• {r}</div>)}
            <div style={{ marginTop: 6, fontWeight: 600 }}>Primary source per link type</div>
            <div>• AWS Site-to-Site VPN: AWS CloudWatch TunnelState per tunnel (fetched hourly). The live "Now" status uses AWS VgwTelemetry polled every 5 minutes.</div>
            <div>• WatchGuard BOVPN: Jakarta Firebox SNMP IPsec SA table every 5 minutes. No independent source exists, so these links are always "Single source".</div>
            <div>• GRE PGOC: SSM check run inside the EC2 host every 5 minutes (ping to the GRE peer and public egress IP).</div>
            <div style={{ marginTop: 6, fontWeight: 600 }}>Confidence badges</div>
            {Object.entries(CONF).map(([k, x]) => <div key={k}><ConfBadge c={k} /> {x.tip}</div>)}
            <div style={{ marginTop: 6, color: "#64748b" }}>Target availability: {data.target}% per link. Verdict "Needs attention" = down now, redundancy lost now, availability below target, little data, or sources disagree.</div>
          </div>
        </Collapsible>
      </>}
    </div>
  );
}

// Raw panels kept for investigation. Mounted only when expanded, so they do not load on every visit.
export function RawSources() {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em", marginTop: 6 }}>Raw sources — for investigation</div>
      <div style={{ fontSize: 12, color: "#64748b", marginTop: -4 }}>These panels show each source as it is, with its own rules. Use the health table above for verdicts and availability.</div>
      <Collapsible title="Live polls (every 5 min)" sub="GRE check, AWS VgwTelemetry per VPN, AD TCP probe — status per poll">
        <GreTunnelPanel />
      </Collapsible>
      <Collapsible title="Jakarta Firebox — BOVPN routes and interfaces" sub="SNMP; a route without an SA is idle, not down">
        <BovpnJakartaMap />
      </Collapsible>
      <Collapsible title="AWS CloudWatch per tunnel" sub="includes unused 2nd tunnels; one tunnel down does not mean the VPN is down">
        <VpnCloudWatchPanel />
      </Collapsible>
      <Collapsible title="Per-tunnel daily availability (VPN email report rules)" sub="per tunnel / per BOVPN route, flaps under 15 min ignored">
        <VpnAvailabilityHistory />
      </Collapsible>
      <Collapsible title="AWS VPN path analysis" sub="routing symmetry and overlapping static prefixes (PIL account)">
        <VpnPathPanel />
      </Collapsible>
    </div>
  );
}
